'use client';

// TODO: this is a component very specifically made for only one instance and not appliable to
// other situations. In these cases it shouldn't be on UI library or it should be more generic
//
import { useEffect, useRef, useState } from 'react';

import {
  Select as ArkSelect,
  Portal,
  SelectItemGroupLabel,
} from '@ark-ui/react';
import type { SelectProps as ArkSelectProps } from '@ark-ui/react';
import {
  CheckmarkCircle20Filled,
  ChevronDown20Filled,
  ChevronUp20Filled,
} from '@fluentui/react-icons';
import { X } from 'lucide-react';

import { cn, floatingLabelStyles } from '@avodah-inc/ui/utils';

import { ScrollArea } from '../scroll-area';

export type ActionableSelectOption = {
  label: string;
  value: string;
  isAction?: boolean;
};

export type BaseActionableSelectProps<T extends ActionableSelectOption> =
  ArkSelectProps<T> & {
    clearable?: boolean;
    showValue?: boolean;
    placeholder?: string;
    label?: string;
    isInvalid?: boolean;
    disableValueTruncation?: boolean;
    showCheckIcon?: boolean;
    mainAxisOffset?: number;
    classNames?: {
      text?: string;
      trigger?: string;
      content?: string;
    };
    onActionSubmit?:
      | ((value: string) => Promise<boolean>)
      | ((value: string) => boolean);
    actionDisabled?: string;
    itemsGroupLabel?: string;
    actionGroupLabel?: string;
    actionHeader?: string;
    actionButtonText?: string;
    inputPlaceholder?: string;
    forceSelectedValue?: string;
    error?: string;
    setError?: (error: string) => void;
  };

export const BaseActionableSelect = <T extends ActionableSelectOption>({
  items,
  placeholder,
  showValue = true,
  clearable = true,
  disableValueTruncation = false,
  label,
  isInvalid,
  classNames,
  showCheckIcon = true,
  mainAxisOffset = 12,
  onActionSubmit,
  actionDisabled,
  itemsGroupLabel,
  actionGroupLabel,
  actionHeader,
  actionButtonText,
  inputPlaceholder,
  forceSelectedValue,
  error,
  ...props
}: BaseActionableSelectProps<T>) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [popoverPlacement, setPopoverPlacement] = useState('bottom');
  const [selectedValue, setSelectedValue] = useState(
    props.value?.[0] || props.defaultValue,
  );
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const nonActionItems = items.filter((item) => !item.isAction);
  const actionItems = items.filter((item) => item.isAction);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectPositionerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isSubmenuOpen) {
      setNewTemplateName('');
    }
  }, [isSubmenuOpen]);

  useEffect(() => {
    setSelectedValue(props.value);
  }, [props.value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectPositionerRef.current &&
        !selectPositionerRef.current.contains(event.target as Node)
      ) {
        setIsSubmenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isSubmenuOpen) {
      inputRef.current?.focus();
      props.setError?.(''); // reset any errors when the action modal is opened
    }
  }, [isSubmenuOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === ' ' ||
        event.key === 'Spacebar' ||
        event.code === 'Space'
      ) {
        event.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  useEffect(() => {
    if (
      forceSelectedValue !== undefined &&
      forceSelectedValue !== selectedValue
    ) {
      setSelectedValue(forceSelectedValue);
    }
  }, [forceSelectedValue]);

  return (
    <ArkSelect.Root
      onClick={() => {
        const placement = triggerRef.current?.getAttribute('data-placement');
        if (placement) setPopoverPlacement(placement);
      }}
      items={items}
      positioning={{
        sameWidth: true,
        offset: {
          mainAxis: mainAxisOffset,
        },
      }}
      className={cn('relative', props.className)}
      {...props}
    >
      <ArkSelect.Control className="relative">
        <ArkSelect.Trigger
          ref={triggerRef}
          data-invalid={isInvalid}
          className={cn(
            'text-body-3 data-[invalid=true]:border-red focus:border-primary group flex h-auto w-full items-center justify-between gap-2 rounded border border-gray-300 bg-white px-4 py-2 text-gray-900 disabled:cursor-not-allowed',
            'data-[state=open]:data-[placement=bottom]:rounded-b-none data-[state=open]:data-[placement=bottom]:border-b-0',
            'data-[state=open]:data-[placement=top]:rounded-t-none data-[state=open]:data-[placement=top]:border-t-0',
            classNames?.trigger,
          )}
        >
          {label && (
            <ArkSelect.Label
              className={cn(
                floatingLabelStyles as string,
                'group-data-[state=open]:group-data-[placement=top]:hidden',
                classNames?.trigger,
              )}
            >
              {label}
            </ArkSelect.Label>
          )}
          {showValue ? (
            <ArkSelect.ValueText
              placeholder={placeholder}
              className={cn(
                'truncate group-disabled:opacity-50',
                disableValueTruncation ? 'flex-wrap whitespace-normal' : '',
                selectedValue ? 'text-gray-900' : 'text-gray-500',
                classNames?.text,
              )}
            />
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
          <ArkSelect.Indicator className="group flex items-center">
            <ChevronDown20Filled className="text-gray-400 transition-all group-disabled:opacity-50 group-data-[state=open]:rotate-180" />
          </ArkSelect.Indicator>
        </ArkSelect.Trigger>
        {selectedValue && clearable && (
          <ArkSelect.ClearTrigger
            onClick={() => {
              setSelectedValue('');
            }}
            className="transform-all absolute right-5 top-3 bg-white active:translate-y-0.5"
          >
            <X size={16} className="text-gray-400" />
          </ArkSelect.ClearTrigger>
        )}

        <Portal>
          <ArkSelect.Positioner ref={selectPositionerRef}>
            <ArkSelect.Content
              className={cn(
                'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out z-[100] h-fit overflow-hidden rounded border border-gray-300 bg-white outline-none',
                {
                  'shadow-surface-2 data-[state=open]:rounded-t-none':
                    popoverPlacement === 'bottom',
                  'data-[state=open]:rounded-b-none':
                    popoverPlacement === 'top',
                },
                classNames?.content,
              )}
            >
              <ScrollArea
                className={cn({
                  'h-[12.5rem]': items.length > 5,
                })}
              >
                <ArkSelect.ItemGroup id="framework">
                  {/* Group for non-action items */}
                  {nonActionItems.length > 0 && (
                    <>
                      <SelectItemGroupLabel
                        htmlFor="framework"
                        className="font-nunito border-b border-t bg-gray-50 px-4 py-2 text-[10px] font-bold uppercase leading-[20px] tracking-[0.96px] text-gray-400"
                      >
                        {itemsGroupLabel}
                      </SelectItemGroupLabel>
                      {nonActionItems.map((item) => (
                        <ArkSelect.Item
                          onClick={() => {
                            setSelectedValue(item.value);
                          }}
                          key={item.value}
                          item={item}
                          className="hover:bg-primary-50 focus:outline-primary-300 group flex cursor-pointer justify-between gap-2 px-4 py-2 outline-none focus:outline-1 focus:outline-offset-1"
                        >
                          <ArkSelect.ItemText className="group-selected:font-medium flex items-center gap-2 truncate font-normal text-gray-900">
                            <p className="truncate">{item.label}</p>
                          </ArkSelect.ItemText>

                          {selectedValue?.includes(item.value) &&
                            showCheckIcon && (
                              <ArkSelect.ItemIndicator className="text-primary-500 flex w-5 items-center">
                                <CheckmarkCircle20Filled />
                              </ArkSelect.ItemIndicator>
                            )}
                        </ArkSelect.Item>
                      ))}
                    </>
                  )}

                  {actionItems.length > 0 && (
                    <>
                      <SelectItemGroupLabel
                        htmlFor="framework"
                        className="font-nunito border-b border-t bg-gray-50 px-4 py-2 text-[10px] font-bold uppercase leading-[20px] tracking-[0.96px] text-gray-400"
                      >
                        {actionGroupLabel}
                      </SelectItemGroupLabel>
                      {actionItems.map((item) => (
                        <div
                          key={item.value}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (actionDisabled) return;
                            setIsSubmenuOpen(true);
                          }}
                          title={actionDisabled}
                          className={cn(
                            'hover:bg-primary-50 focus:outline-primary-300 text-primary-500 flex cursor-pointer justify-between gap-2 px-4 py-2 outline-none',
                            {
                              'bg-primary-50 text-primary-500': isSubmenuOpen,
                              'cursor-not-allowed bg-white italic text-gray-300':
                                actionDisabled,
                            },
                          )}
                        >
                          <p className="truncate capitalize">{item.label}</p>
                          <ChevronUp20Filled
                            className={cn({ 'rotate-90': isSubmenuOpen })}
                          />
                        </div>
                      ))}
                    </>
                  )}
                </ArkSelect.ItemGroup>

                {isSubmenuOpen && !actionDisabled && (
                  <div
                    className=" fixed -bottom-14 left-52 flex w-64 flex-col items-start gap-3 self-stretch rounded border border-gray-300 bg-white p-3 shadow-lg"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <div className="absolute -left-2 top-4 -z-10 h-3.5 w-3.5 rotate-[-135deg] transform border-r border-t border-gray-300 bg-white" />

                    <div className="flex w-full justify-between pb-1">
                      <h2>{actionHeader}</h2>
                      <button
                        onClick={() => {
                          setIsSubmenuOpen(false);
                        }}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                      >
                        <X />
                      </button>
                    </div>
                    {error ? (
                      <span className="text-status-alert -my-2 text-sm">
                        {error}
                      </span>
                    ) : null}
                    <div className="mb-1 flex w-full items-center justify-between">
                      <input
                        type="text"
                        ref={inputRef}
                        value={newTemplateName}
                        onChange={(e) => {
                          props.setError?.('');
                          setNewTemplateName(e.target.value);
                        }}
                        onKeyDown={(e) =>
                          void (async () => {
                            e.stopPropagation();
                            if (e.key !== 'Enter') {
                              return;
                            }

                            e.preventDefault();
                            setIsSubmenuOpen(
                              (await onActionSubmit?.(newTemplateName)) ??
                                false,
                            );
                            setNewTemplateName('');
                          })()
                        }
                        placeholder={inputPlaceholder}
                        className={cn(
                          'focus:ring-primary-500 w-full rounded border px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-1',
                          {
                            'outline-status-alert': Boolean(error),
                          },
                        )}
                      />
                      {newTemplateName && (
                        <button
                          onClick={() =>
                            void (async () => {
                              setIsSubmenuOpen(
                                (await onActionSubmit?.(newTemplateName)) ??
                                  false,
                              );
                              setNewTemplateName('');
                            })()
                          }
                          className="bg-primary-500 hover:bg-primary-600 ml-2 rounded px-3 py-1 text-white"
                        >
                          {actionButtonText}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </ScrollArea>
            </ArkSelect.Content>
          </ArkSelect.Positioner>
        </Portal>
      </ArkSelect.Control>
    </ArkSelect.Root>
  );
};

export type ActionableSelectProps<T extends ActionableSelectOption> = Omit<
  BaseActionableSelectProps<T>,
  'multiple'
> & {
  onValueChange?: (e: { item: T; value: string }) => void;
  error?: string;
  setError?: (error: string) => void;
};

export const ActionableSelect = <T extends ActionableSelectOption>({
  onValueChange,
  ...props
}: ActionableSelectProps<T>) => {
  return (
    <BaseActionableSelect
      {...props}
      onValueChange={(e) => {
        if (!e.items[0]) return;
        onValueChange?.({
          item: e.items[0],
          value: e.value[0] ?? '',
        });
      }}
    />
  );
};
